/**
 * Removes emoji / pictographs (e.g. from Windows Win+. picker).
 */
export function stripEmoji(value: string): string {
  if (!value) {
    return value;
  }

  return value
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/\p{Emoji_Modifier}/gu, '')
    .replace(/[\uFE0F\u200D\u20E3]/g, '');
}

export type InputFilterKind =
  | 'emoji'
  | 'digits'
  | 'mobile'
  | 'decimal'
  | 'letters'
  | 'alphanumeric'
  | 'alphanumeric-space'
  | 'alphanumeric-hyphen'
  | 'email'
  | 'pan';

const EXPLICIT_FILTERS: Record<string, InputFilterKind> = {
  emoji: 'emoji',
  digits: 'digits',
  number: 'digits',
  numeric: 'digits',
  otp: 'digits',
  mobile: 'mobile',
  phone: 'mobile',
  decimal: 'decimal',
  letters: 'letters',
  alpha: 'letters',
  name: 'letters',
  alphanumeric: 'alphanumeric',
  alphanum: 'alphanumeric',
  'alphanumeric-space': 'alphanumeric-space',
  address: 'alphanumeric-space',
  'alphanumeric-hyphen': 'alphanumeric-hyphen',
  'client-id': 'alphanumeric-hyphen',
  email: 'email',
  pan: 'pan',
};

/**
 * Resolve allowed-character rule for an input.
 * Prefer `data-input-filter` when present; otherwise infer from type / name / id / placeholder / pattern.
 */
export function resolveInputFilter(el: HTMLInputElement | HTMLTextAreaElement): InputFilterKind {
  const explicit = (el.getAttribute('data-input-filter') || '').toLowerCase().trim();
  if (explicit && EXPLICIT_FILTERS[explicit]) {
    return EXPLICIT_FILTERS[explicit];
  }

  if (el instanceof HTMLTextAreaElement) {
    return 'emoji';
  }

  const type = (el.type || 'text').toLowerCase();
  if (type === 'password' || type === 'search' || type === 'url') {
    return 'emoji';
  }

  const inputMode = (el.inputMode || '').toLowerCase();
  const meta = `${el.name || ''} ${el.id || ''} ${el.placeholder || ''} ${el.className || ''}`.toLowerCase();
  const pattern = (el.getAttribute('pattern') || '').trim();
  const maxLength = el.maxLength > 0 ? el.maxLength : 0;

  // Field-purpose heuristics first (some OTP fields are wrongly typed as email)
  if (/otp|one[-\s_]?time|verification\s*code|enter otp|digit code|mobileotp|emailotp/.test(meta)) {
    return 'digits';
  }
  if (/(^|[^a-z])pan([^a-z]|$)|pannumber|pan_number|nomineepan|guardianpan/.test(meta)) {
    return 'pan';
  }
  if (/aadhar|aadhaar/.test(meta)) {
    return 'digits';
  }
  if (/pincode|pin_?code|zipcode|zip_?code/.test(meta)) {
    return 'digits';
  }
  if (
    pattern === '\\d*' ||
    pattern === '[0-9]*' ||
    pattern === '[0-9]+' ||
    pattern === '\\d+' ||
    pattern === '[0-9]{6}' ||
    pattern === '\\d{6}'
  ) {
    return 'digits';
  }
  if (
    /(firstname|lastname|middlename|fullname|clientname|guardianname|nomineename|full_?name)/.test(meta) ||
    /(^|_)(first|last|middle|full|guardian|nominee)?names?$/.test((el.name || el.id || '').toLowerCase())
  ) {
    return 'letters';
  }
  if (/address/.test(meta)) {
    return 'alphanumeric-space';
  }
  if (/mobile|phone|phonenumber|mobilenumber|partnernumber|recipientmobile|familyclientmobile/.test(meta)) {
    // OTP fields often include "mobile" in the name — keep as digits only
    if (/otp|one[-\s_]?time|verification\s*code/.test(meta) || (maxLength > 0 && maxLength < 10)) {
      return 'digits';
    }
    return 'mobile';
  }
  // Common OTP shape: short numeric code field
  if (maxLength >= 3 && maxLength <= 8 && (/^[0-9]+$/.test((el.placeholder || '').trim()) || inputMode === 'numeric')) {
    return 'digits';
  }

  if (type === 'email' || inputMode === 'email' || /email/.test(meta)) {
    return 'email';
  }
  if (type === 'number') {
    return 'decimal';
  }
  if (inputMode === 'decimal') {
    return 'decimal';
  }
  if (type === 'tel' || inputMode === 'numeric' || inputMode === 'tel') {
    return 'digits';
  }

  return 'emoji';
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) {
    return cleaned;
  }
  return `${parts[0]}.${parts.slice(1).join('')}`;
}

/** Strip emoji, then apply field-specific allowed characters. */
export function sanitizeInputValue(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string
): string {
  let next = stripEmoji(value ?? '');
  const kind = resolveInputFilter(el);

  switch (kind) {
    case 'digits':
      return next.replace(/[^0-9]/g, '');
    case 'mobile':
      return next.replace(/[^0-9]/g, '').slice(0, 10);
    case 'decimal':
      return sanitizeDecimal(next);
    case 'letters':
      return next.replace(/[^a-zA-Z\s]/g, '');
    case 'alphanumeric':
      return next.replace(/[^a-zA-Z0-9]/g, '');
    case 'alphanumeric-space':
      return next.replace(/[^a-zA-Z0-9\s]/g, '');
    case 'alphanumeric-hyphen':
      return next.replace(/[^a-zA-Z0-9-]/g, '');
    case 'email':
      return next.replace(/[^a-zA-Z0-9@._%+\-]/g, '');
    case 'pan': {
      const maxLen = el.maxLength > 0 ? el.maxLength : 10;
      return next.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, maxLen);
    }
    case 'emoji':
    default:
      return next;
  }
}

export function isTextEntryElement(target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  if (target.hasAttribute('data-skip-input-filter')) {
    return false;
  }

  if (target instanceof HTMLTextAreaElement) {
    return !target.readOnly && !target.disabled;
  }

  if (!(target instanceof HTMLInputElement)) {
    return false;
  }

  if (target.readOnly || target.disabled) {
    return false;
  }

  const type = (target.type || 'text').toLowerCase();
  const blockedTypes = new Set([
    'checkbox',
    'radio',
    'file',
    'hidden',
    'button',
    'submit',
    'reset',
    'image',
    'color',
    'range',
    'date',
    'datetime-local',
    'time',
    'week',
    'month',
  ]);

  return !blockedTypes.has(type);
}
